import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { URL as API_BASE } from '../env';

// Set the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const THUMBNAIL_SCALE = 0.2;

/**
 * PdfViewer - Renders a PDF using PDF.js with clickable links, sidebar thumbnails,
 * page navigation, download and print support.
 * Fetches PDF through backend proxy to bypass S3 CORS restrictions.
 *
 * Props:
 *  - src: S3 URL of the PDF to render
 *  - className: CSS class for the outer container
 *  - style: inline style for the outer container
 *  - title: accessible title (also used as download filename)
 */
export default function PdfViewer({ src, className = '', style = {}, title = 'PDF Viewer' }) {
    const containerRef = useRef(null);
    const mainScrollRef = useRef(null);
    const pageRefsRef = useRef([]);
    const observerRef = useRef(null);
    const visibilityMapRef = useRef({});
    const renderTasksRef = useRef([]);
    const pagesRenderedRef = useRef(false);

    const [pdfDoc, setPdfDoc] = useState(null);
    const [pdfBytes, setPdfBytes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scale, setScale] = useState(1.5);
    const [currentPage, setCurrentPage] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [thumbnails, setThumbnails] = useState([]);
    const [pageInputValue, setPageInputValue] = useState('1');

    // Load the PDF document via backend proxy
    useEffect(() => {
        if (!src) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setPdfDoc(null);
        setPdfBytes(null);
        setThumbnails([]);
        setCurrentPage(1);
        setPageInputValue('1');
        pagesRenderedRef.current = false;
        pageRefsRef.current = [];
        visibilityMapRef.current = {};

        const proxyUrl = `${API_BASE}/api/company/pdf-proxy?url=${encodeURIComponent(src)}`;

        fetch(proxyUrl)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.arrayBuffer();
            })
            .then(data => {
                if (cancelled) return;
                // Store a copy before PDF.js transfers/detaches the ArrayBuffer
                setPdfBytes(data.slice(0));
                return pdfjsLib.getDocument({
                    data: new Uint8Array(data),
                    cMapUrl: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
                    cMapPacked: true,
                }).promise;
            })
            .then(doc => {
                if (!cancelled) {
                    setPdfDoc(doc);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    console.error('PDF load error:', err);
                    setError('Failed to load PDF');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [src]);

    // Render all pages
    const renderPages = useCallback(async () => {
        if (!pdfDoc || !containerRef.current || pagesRenderedRef.current) return;
        pagesRenderedRef.current = true;

        const container = containerRef.current;
        renderTasksRef.current.forEach(t => t.cancel?.());
        renderTasksRef.current = [];
        container.innerHTML = '';
        pageRefsRef.current = [];
        visibilityMapRef.current = {};

        if (observerRef.current) observerRef.current.disconnect();

        // Track which page is most visible in the scroll area
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    const pageNum = parseInt(entry.target.dataset.pageNum, 10);
                    visibilityMapRef.current[pageNum] = entry.intersectionRatio;
                });
                let maxRatio = 0;
                let mostVisible = 1;
                Object.entries(visibilityMapRef.current).forEach(([num, ratio]) => {
                    if (ratio > maxRatio) {
                        maxRatio = ratio;
                        mostVisible = parseInt(num, 10);
                    }
                });
                setCurrentPage(mostVisible);
                setPageInputValue(String(mostVisible));
            },
            { root: mainScrollRef.current, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0] }
        );

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
            try {
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale });

                const pageDiv = document.createElement('div');
                pageDiv.style.cssText = `position:relative;width:${viewport.width}px;height:${viewport.height}px;margin:0 auto 8px auto;`;
                pageDiv.dataset.pageNum = pageNum;

                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.display = 'block';
                pageDiv.appendChild(canvas);
                container.appendChild(pageDiv);

                pageRefsRef.current[pageNum - 1] = pageDiv;
                observerRef.current.observe(pageDiv);

                const ctx = canvas.getContext('2d');
                const renderTask = page.render({ canvasContext: ctx, viewport });
                renderTasksRef.current.push(renderTask);
                await renderTask.promise;

                // Overlay clickable link annotations
                const annotations = await page.getAnnotations();
                const linkAnnotations = annotations.filter(a => a.subtype === 'Link' && a.url);
                if (linkAnnotations.length > 0) {
                    const annotLayer = document.createElement('div');
                    annotLayer.style.cssText = `position:absolute;top:0;left:0;width:${viewport.width}px;height:${viewport.height}px;`;

                    linkAnnotations.forEach(annotation => {
                        const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(annotation.rect);
                        const link = document.createElement('a');
                        link.href = annotation.url;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.title = annotation.url;
                        link.style.cssText = `position:absolute;left:${Math.min(x1,x2)}px;top:${Math.min(y1,y2)}px;width:${Math.abs(x2-x1)}px;height:${Math.abs(y2-y1)}px;cursor:pointer;display:block;background:transparent;border:none;z-index:1;`;
                        link.addEventListener('mouseenter', () => {
                            link.style.backgroundColor = 'rgba(0,100,255,0.1)';
                            link.style.borderBottom = '2px solid rgba(0,100,255,0.4)';
                        });
                        link.addEventListener('mouseleave', () => {
                            link.style.backgroundColor = 'transparent';
                            link.style.borderBottom = 'none';
                        });
                        annotLayer.appendChild(link);
                    });
                    pageDiv.appendChild(annotLayer);
                }
            } catch (err) {
                if (err.name !== 'RenderingCancelledException') {
                    console.error(`Error rendering page ${pageNum}:`, err);
                }
            }
        }
    }, [pdfDoc, scale]);

    // Render thumbnails once per PDF load (not on scale change)
    useEffect(() => {
        if (!pdfDoc) return;
        let cancelled = false;
        setThumbnails([]);

        (async () => {
            for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
                if (cancelled) break;
                try {
                    const page = await pdfDoc.getPage(pageNum);
                    const viewport = page.getViewport({ scale: THUMBNAIL_SCALE });
                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                    if (!cancelled) {
                        const dataUrl = canvas.toDataURL();
                        setThumbnails(prev => [...prev, { pageNum, dataUrl }]);
                    }
                } catch (err) {
                    // skip failed thumbnail
                }
            }
        })();

        return () => { cancelled = true; };
    }, [pdfDoc]);

    // Render pages when doc loads or scale changes
    useEffect(() => {
        if (pdfDoc) {
            pagesRenderedRef.current = false;
            renderPages();
        }
    }, [pdfDoc, scale, renderPages]);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => { if (observerRef.current) observerRef.current.disconnect(); };
    }, []);

    const scrollToPage = useCallback((pageNum) => {
        const pageDiv = pageRefsRef.current[pageNum - 1];
        if (pageDiv) pageDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const handleDownload = useCallback(() => {
        if (!pdfBytes) return;
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = title !== 'PDF Viewer' ? `${title}.pdf` : 'document.pdf';
        a.click();
        URL.revokeObjectURL(url);
    }, [pdfBytes, title]);

    const handlePrint = useCallback(() => {
        if (!pdfBytes) return;
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (win) {
            win.addEventListener('load', () => {
                win.print();
                URL.revokeObjectURL(url);
            });
        }
    }, [pdfBytes]);

    const handlePageInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const num = parseInt(pageInputValue, 10);
            if (pdfDoc && num >= 1 && num <= pdfDoc.numPages) {
                scrollToPage(num);
            } else {
                setPageInputValue(String(currentPage));
            }
        }
    };

    const handlePageInputBlur = () => {
        const num = parseInt(pageInputValue, 10);
        if (!pdfDoc || isNaN(num) || num < 1 || num > pdfDoc.numPages) {
            setPageInputValue(String(currentPage));
        }
    };

    if (!src) return null;

    const numPages = pdfDoc?.numPages ?? 0;

    return (
        <div
            className={className}
            style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#525659', overflow: 'hidden', ...style }}
            title={title}
        >
            {/* Toolbar */}
            <div style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', backgroundColor: '#323639', borderBottom: '1px solid #474a4d',
                zIndex: 10, gap: '8px',
            }}>
                {/* Left: sidebar toggle */}
                <button
                    onClick={() => setSidebarOpen(o => !o)}
                    style={toolbarBtnStyle}
                    title={sidebarOpen ? 'Hide Thumbnails' : 'Show Thumbnails'}
                >☰</button>

                {/* Center: page indicator + zoom */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, justifyContent: 'center' }}>
                    <input
                        type="text"
                        value={pageInputValue}
                        onChange={e => setPageInputValue(e.target.value)}
                        onKeyDown={handlePageInputKeyDown}
                        onBlur={handlePageInputBlur}
                        style={{
                            width: '38px', textAlign: 'center', background: '#474a4d',
                            border: '1px solid #5a5d60', color: '#ddd', borderRadius: '4px',
                            padding: '3px 4px', fontSize: '13px',
                        }}
                    />
                    <span style={{ color: '#888', fontSize: '13px' }}>/ {numPages || '—'}</span>

                    <div style={{ width: '1px', height: '18px', background: '#5a5d60', margin: '0 4px' }} />

                    <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))} style={toolbarBtnStyle} title="Zoom Out">−</button>
                    <span style={{ color: '#ccc', fontSize: '13px', minWidth: '44px', textAlign: 'center' }}>
                        {Math.round(scale * 100)}%
                    </span>
                    <button onClick={() => setScale(s => Math.min(3, s + 0.25))} style={toolbarBtnStyle} title="Zoom In">+</button>
                    <button
                        onClick={() => setScale(1.5)}
                        style={{ ...toolbarBtnStyle, fontSize: '12px', padding: '4px 10px' }}
                        title="Reset Zoom"
                    >Reset</button>
                </div>

                {/* Right: download + print */}
                <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                        onClick={handleDownload}
                        style={{ ...toolbarBtnStyle, fontSize: '12px', padding: '4px 12px' }}
                        title="Download PDF"
                        disabled={!pdfBytes}
                    >Download</button>
                    <button
                        onClick={handlePrint}
                        style={{ ...toolbarBtnStyle, fontSize: '12px', padding: '4px 12px' }}
                        title="Print PDF"
                        disabled={!pdfBytes}
                    >Print</button>
                </div>
            </div>

            {/* Body: sidebar + main scroll area */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Thumbnail sidebar */}
                {sidebarOpen && (
                    <div style={{
                        width: '150px', flexShrink: 0, overflowY: 'auto',
                        backgroundColor: '#3a3d40', borderRight: '1px solid #474a4d', padding: '8px 0',
                    }}>
                        {thumbnails.length === 0 && (
                            <div style={{ color: '#888', fontSize: '11px', textAlign: 'center', padding: '16px 8px' }}>
                                {loading ? 'Loading...' : 'Loading thumbnails...'}
                            </div>
                        )}
                        {thumbnails.map(({ pageNum, dataUrl }) => (
                            <div
                                key={pageNum}
                                onClick={() => scrollToPage(pageNum)}
                                style={{
                                    padding: '6px 8px', cursor: 'pointer',
                                    backgroundColor: currentPage === pageNum ? '#525659' : 'transparent',
                                    borderLeft: currentPage === pageNum ? '3px solid #6ab0f5' : '3px solid transparent',
                                    transition: 'background 0.1s',
                                }}
                                title={`Page ${pageNum}`}
                            >
                                <img
                                    src={dataUrl}
                                    alt={`Page ${pageNum}`}
                                    style={{ width: '100%', display: 'block', border: '1px solid #555', borderRadius: '2px' }}
                                />
                                <div style={{ color: '#aaa', fontSize: '10px', textAlign: 'center', marginTop: '3px' }}>
                                    {pageNum}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Main PDF scroll area */}
                <div ref={mainScrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                    {/* Loading state */}
                    {loading && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#ccc' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', marginBottom: '8px' }}>Loading PDF...</div>
                                <div style={{ width: '40px', height: '40px', border: '3px solid #555', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                        </div>
                    )}

                    {/* Error state */}
                    {error && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#ff6b6b' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', marginBottom: '8px' }}>{error}</div>
                                <button onClick={() => window.open(src, '_blank')} style={{ ...toolbarBtnStyle, padding: '6px 16px' }}>
                                    Open PDF directly
                                </button>
                            </div>
                        </div>
                    )}

                    {/* PDF pages container */}
                    <div ref={containerRef} style={{ padding: '16px 0' }} />
                </div>
            </div>
        </div>
    );
}

const toolbarBtnStyle = {
    background: '#474a4d',
    border: '1px solid #5a5d60',
    color: '#ddd',
    padding: '4px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '1',
};
